import { HubConnectionState } from "@microsoft/signalr";
import { loadWrapper } from "aic";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { SignalRContext } from "contexts/SignalRProvider";
import { useUnsavedChanges } from "contexts/UnsavedChangesContextProvider";
import { getHierarchyBaseUrl } from "contexts/hierarchy/getHierarchyBaseUrl";
import { UPDATE_HIERARCHY } from "contexts/hierarchy/interface";
import { TocActionSignal } from "contexts/signalr/interface";
import { getTlfUrl } from "contexts/tlf/getTlfUrl";
import { UPDATE_TLF } from "contexts/tlf/interface";
import { prepareTlfSave } from "contexts/tlf/prepareTlfSave";
import { sendSignalRPatch } from "contexts/tlf/sendSignalRPatch";
import {
  MosaicTocEntryDto,
  MosaicTocSectionDto,
  SAVE_FAIL,
  SAVE_SUCCESS,
  TocUpdateMessage,
  UPDATE_ENTRY,
} from "contexts/toc/interface";
import {
  eAccessRights,
  eApplicationPermission,
  eHierarchy,
  eHierarchyLevel,
  eReleaseStatus,
  eTlfItemType,
} from "functions/api/enums";
import { loadHierarchy } from "functions/api/hierarchy/loadHierarchy";
import { restoreBackupReport } from "functions/api/hierarchy/restoreBackupReport";
import { saveHierarchyData } from "functions/api/hierarchy/saveHierarchyData";
import { toHierarchyUpdateDto } from "functions/api/hierarchy/toHierarchyUpdateDto";
import { IRequestStatus } from "functions/api/interface";
import { isTlfItemReadDto, TlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { loadTlfItemById } from "functions/api/tlfItem/loadTlfItemById";
import { patchTlfItem } from "functions/api/tlfItem/patchTlfItem";
import { SaveNewTlfItem } from "functions/api/tlfItem/saveNewTlfItem";
import { saveTlfItem, SaveTlfItemDetails } from "functions/api/tlfItem/saveTlfItem";
import { TocItemsUpdateProps, updateTocItems } from "functions/api/toc/updateTocItems";
import { doReload } from "functions/doReload";
import { isCrystalReferenceValid } from "functions/validateCrystalTicketNumber";
import { useContext } from "react";
import { SAVE_COMPLETED, SET_SAVE_REQUEST } from "./interface";

/**
 * Presentation needed by the combined hierarchy/TLF Save control.
 */
interface SaveButtonState {
  action: "save" | "template";
  disabled: boolean;
  label: string;
  show: boolean;
  title?: string;
  variant?: string;
}

/** Labels describing the current TLF Save permission or validation outcome. */
enum SaveLabels {
  Save = "Save",
  TlfCannotSaveBlDomain = "Cannot save with BL domain",
  TlfCannotSaveCrystalReference = "Cannot save with invalid CRYSTAL reference",
  TlfCannotSaveNew = "Use save as for new TLF items",
  TlfCannotSaveReleased = "Use Save As for released TLFs",
  TlfSaveAndRevert = "Save and revert to draft",
  TlfUpdateTemplate = "Update new TLF item template",
}

/**
 * Routes changed hierarchy and TLF data to the appropriate save API.
 * Request status belongs only to AppSettingsContext.state and is updated by loadWrapper.
 * ARS collections are already synchronised into hierarchy state before Save is available.
 *
 * @returns Save presentation, application request status and save commands.
 */
export const useSave = () => {
  const { state, dispatch } = useContext(AppSettingsContext);
  const { hierarchyState, tlfState, tocState, saveRequest, backEndUrl } = state;
  const hierarchy = hierarchyState.hierarchy;
  const tlf = isTlfItemReadDto(tlfState.tlfData) ? tlfState.tlfData : null;
  const signalR = useContext(SignalRContext);
  const { navigateAfterSave } = useUnsavedChanges("app-save");

  /**
   * Updates the single request status used by every save control.
   *
   * @param status - Request lifecycle information supplied by loadWrapper.
   */
  const setSaveRequest = (status: IRequestStatus<unknown>) =>
    dispatch({ operation: SET_SAVE_REQUEST, saveRequest: status });

  /**
   * Saves TOC edits or refreshes the hierarchy version using the shared save request.
   *
   * @param tocItemsToUpdate - Changed TOC entries and an optional deleted item.
   * @returns Sent collaboration metadata for the route's SignalR echo tracking, when sent.
   */
  const saveTocItems = async (
    tocItemsToUpdate: TocItemsUpdateProps,
  ): Promise<TocUpdateMessage | undefined> => {
    let sentMessage: TocUpdateMessage | undefined;
    if (hierarchy && !saveRequest.requesting) {
      const changeList = tocState.tocItems.filter((item) => item.changed).map((item) => item.id);
      const response = await loadWrapper(
        updateTocItems,
        tocItemsToUpdate,
        { ...saveRequest, error: false, requestedId: undefined },
        setSaveRequest,
        backEndUrl,
      );
      if (response?.success) {
        const newHierarchy = {
          ...hierarchy,
          tocMajorVersion: response.data.tocMajorVersion,
          tocMinorVersion: response.data.tocMinorVersion,
          sections: response.data.sections,
        };
        dispatch({
          operation: UPDATE_HIERARCHY,
          hierarchy: newHierarchy,
          isSynchronisation: true,
        });
        dispatch({
          operation: SAVE_SUCCESS,
          itemsUpdate: tocItemsToUpdate,
          saveTimeStamp: Date.now(),
        });
        if (signalR) {
          const signalRUpdate: TocActionSignal = {
            hierarchy: newHierarchy,
            items: [
              ...response.data.sections
                .filter((item) => changeList.includes(item.id))
                .map((section): MosaicTocSectionDto => ({
                  id: section.id,
                  primaryHierarchyId: newHierarchy.id,
                  tocNumber: section.sectionNumber,
                  tocLabel: section.sectionLabel,
                  type: eTlfItemType.section,
                  tocOrder: section.order,
                  changed: false,
                  inError: false,
                })),
              ...response.data.shells
                .filter((item) => changeList.includes(item.id))
                .map((item): MosaicTocEntryDto => ({
                  ...item,
                  changedTitles: item.changedTitles ?? [],
                  changed: false,
                  inError: false,
                })),
            ],
            deletedItemId: tocItemsToUpdate.deletedItemId,
          };
          const message = await signalR.sendTocUpdateToGroup(signalRUpdate, `toc${hierarchy.id}`);
          sentMessage = {
            messageId: message.messageId,
            updatedIds: ["sentMessage"],
            updateTS: new Date(message.timeStamp),
          };
        }
      } else {
        if (response === null) setSaveRequest({ requesting: false, error: false });
        else console.warn("Save failed", response.errorText);
        dispatch({ operation: SAVE_FAIL });
      }
    }
    return sentMessage;
  };

  /**
   * Persists one TLF field while sharing the application save request lifecycle.
   *
   * @param id - Identifier of the TLF to patch.
   * @param azsolId - AZSOL identifier used to locate the TLF.
   * @param key - Persisted TLF field being edited.
   * @param newValue - Replacement value for the selected field.
   */
  const patchTlf = async <K extends keyof TlfItemReadDto>(
    id: string,
    azsolId: string,
    key: K,
    newValue: TlfItemReadDto[K],
  ): Promise<void> => {
    if (!saveRequest.requesting) {
      const response = await loadWrapper(
        patchTlfItem,
        { id, azsolId, key, newValue },
        { ...saveRequest, error: false, requestedId: undefined },
        setSaveRequest,
        backEndUrl,
      );
      if (response?.success) {
        if (tlfState.tlfData && response.data.id === tlfState.tlfData.id) {
          dispatch({
            operation: UPDATE_TLF,
            calledFrom: "useSave:PatchResponse",
            tlfData: response.data,
            newSavePoint: true,
          });
        }
        dispatch({ operation: UPDATE_ENTRY, update: response.data });
        await sendSignalRPatch(tlfState.savedTlfData, response.data, signalR, dispatch);
      } else if (response === null) setSaveRequest({ requesting: false, error: false });
    }
  };

  /**
   * Saves changed hierarchy data, or TLF data with an optional hierarchy update.
   *
   * @param azsolId - AZSOL identifier when copying the TLF with Save As.
   * @param version - Version assigned to the copied TLF.
   * @param domain - Domain assigned to the copied TLF.
   * @param primaryHierarchyId - Destination hierarchy for Save As.
   * @param additionalInformation - Description assigned to the copied TLF.
   * @param copiedFromHierarchyLevel - Source level recorded for Save As.
   * @param tocNumber - Optional TOC number for the copied TLF.
   * @param sapReference - Optional SAP reference for the copied TLF.
   */
  const save = async (
    azsolId?: string,
    version?: number,
    domain?: string,
    primaryHierarchyId?: string,
    additionalInformation?: string,
    copiedFromHierarchyLevel?: eHierarchyLevel,
    tocNumber?: string,
    sapReference?: string,
  ): Promise<void> => {
    const saveAs = azsolId !== undefined;
    if (
      hierarchy &&
      !saveRequest.requesting &&
      (saveAs || !hierarchy.isBackupVersion) &&
      !(saveAs && hierarchyState.changed)
    ) {
      const submittedHierarchy = toHierarchyUpdateDto(hierarchy);
      const submittedPath = window.location.pathname;
      if (tlf && (saveAs || tlfState.changed)) {
        const prepared = prepareTlfSave(tlfState, tlf);
        if (prepared) {
          const isNewTlf =
            azsolId !== undefined &&
            azsolId !== "" &&
            version !== undefined &&
            additionalInformation !== undefined &&
            primaryHierarchyId !== undefined &&
            primaryHierarchyId !== "";
          const saveDetailsWithoutHierarchy = {
            tlfItem: {
              ...prepared.tlfItem,
              tocNumber: tocNumber ?? prepared.tlfItem.tocNumber,
              sapReference: sapReference ?? prepared.tlfItem.sapReference,
            },
            newDetails: isNewTlf
              ? {
                  azsolId,
                  version,
                  domain: domain ?? "",
                  additionalInformation:
                    additionalInformation ??
                    // istanbul ignore next
                    "",
                  primaryHierarchyId,
                }
              : undefined,
            copiedFromHierarchyLevel,
          };
          const saveDetails: SaveTlfItemDetails =
            !isNewTlf && hierarchyState.changed
              ? {
                  ...saveDetailsWithoutHierarchy,
                  hierarchy: submittedHierarchy,
                  expectedHierarchyLastUpdatedAt: hierarchy.lastUpdatedAt,
                }
              : saveDetailsWithoutHierarchy;
          const response = await loadWrapper(
            saveTlfItem,
            saveDetails,
            { ...saveRequest, error: false, requestedId: undefined },
            setSaveRequest,
            backEndUrl,
          );
          if (response?.success) {
            dispatch({
              operation: SAVE_COMPLETED,
              result: response.data,
              isNewTlf,
              submittedTlfId: tlf.id,
              submittedTlf: tlf,
              submittedHierarchy,
              submittedVariantView: prepared.variantView,
            });
            await sendSignalRPatch(tlfState.savedTlfData, response.data.tlfItem, signalR, dispatch);
            const savedTlf = response.data.tlfItem;
            const targetHierarchy =
              response.data.hierarchy ??
              hierarchyState.hierarchyNodes.find((item) => item.id === savedTlf.primaryHierarchyId);
            const newUrl = targetHierarchy
              ? getTlfUrl(targetHierarchy, savedTlf)
              : `/TlfDesigner/${savedTlf.azsolId}/${savedTlf.version}`;
            if (window.location.pathname === submittedPath && newUrl !== submittedPath)
              navigateAfterSave(newUrl);
          } else if (response?.errorText?.startsWith("Save conflict:") === true) {
            // TODO: Handle this some other way
            const [latestTlf, latestHierarchy] = await Promise.all([
              loadTlfItemById(
                backEndUrl,
                { id: tlf.id, azsolId: tlf.azsolId },
                new AbortController(),
              ),
              loadHierarchy(backEndUrl, { hierarchyId: hierarchy.id }, new AbortController()),
            ]);
            if (latestTlf.success && latestHierarchy.success) {
              const savedTlf = isTlfItemReadDto(tlfState.savedTlfData)
                ? tlfState.savedTlfData
                : tlf;
              const savedHierarchy = hierarchyState.savedHierarchy ?? hierarchy;
              dispatch({
                operation: SAVE_COMPLETED,
                result: { tlfItem: latestTlf.data, hierarchy: latestHierarchy.data },
                isNewTlf: false,
                submittedTlfId: tlf.id,
                submittedTlf: savedTlf,
                submittedHierarchy: toHierarchyUpdateDto(savedHierarchy),
              });
            } else {
              setSaveRequest({
                requesting: false,
                error: true,
                errorText: `${response.errorText} Unable to refresh both documents.`,
              });
            }
          } else if (response === null) setSaveRequest({ requesting: false, error: false });
        }
      } else if (hierarchyState.changed) {
        const response = await loadWrapper(
          saveHierarchyData,
          submittedHierarchy,
          { ...saveRequest, error: false, requestedId: undefined },
          setSaveRequest,
          backEndUrl,
        );
        if (response?.success) {
          dispatch({
            operation: SAVE_COMPLETED,
            result: { hierarchy: response.data },
            isNewTlf: false,
            submittedTlfId: tlfState.tlfData?.id,
            submittedHierarchy,
          });
          const oldBase = getHierarchyBaseUrl(
            hierarchyState.hierarchyNodes.find((item) => item.id === hierarchy.id) ?? hierarchy,
          );
          const newBase = getHierarchyBaseUrl(response.data);
          if (
            !tlf &&
            window.location.pathname === submittedPath &&
            (oldBase !== newBase || hierarchy.id !== response.data.id)
          ) {
            const path = decodeURIComponent(window.location.pathname);
            const subpage = path.startsWith(`${oldBase}/`) ? path.slice(oldBase.length) : "";
            navigateAfterSave(`${newBase}${subpage}`);
          }
        } else if (response === null) setSaveRequest({ requesting: false, error: false });
      }
    }
  };

  /**
   * Restores the selected backup Report using the application save request status.
   */
  const restore = async () => {
    if (
      hierarchy?.isBackupVersion &&
      hierarchy.type === eHierarchy.Report &&
      !saveRequest.requesting
    ) {
      const submittedHierarchy = toHierarchyUpdateDto(hierarchy);
      const response = await loadWrapper(
        restoreBackupReport,
        submittedHierarchy,
        { ...saveRequest, error: false, requestedId: undefined },
        setSaveRequest,
        backEndUrl,
      );
      if (response?.success) {
        dispatch({
          operation: SAVE_COMPLETED,
          result: { hierarchy: response.data },
          isNewTlf: false,
          submittedTlfId: tlfState.tlfData?.id,
          submittedHierarchy,
        });
        if (
          hierarchy.originHierarchyId &&
          signalR?.connection?.state === HubConnectionState.Connected
        )
          await signalR.sendTocSaveToGroup(
            { oldHierarchyId: hierarchy.originHierarchyId, newHierarchy: response.data },
            `toc${hierarchy.originHierarchyId}`,
          );
        navigateAfterSave(getHierarchyBaseUrl(response.data));
      } else if (response === null) setSaveRequest({ requesting: false, error: false });
    }
  };

  /**
   * Updates the new-TLF template and reloads the application after success.
   */
  const saveTemplate = async () => {
    if (tlf && !saveRequest.requesting) {
      const response = await loadWrapper(
        SaveNewTlfItem,
        { tlfItem: { ...tlf, domain: "" } },
        { ...saveRequest, error: false, requestedId: undefined },
        setSaveRequest,
        backEndUrl,
      );
      if (response?.success) doReload();
      else if (response === null) setSaveRequest({ requesting: false, error: false });
    }
  };

  const saveBlocked = state.saveBlockers.size > 0;
  const canSaveHierarchy = hierarchyState.accessRights.includes(
    hierarchy?.level === eHierarchyLevel.Report
      ? eAccessRights.WriteToTlfItemsInHierarchy
      : eAccessRights.MaintainHierarchy,
  );
  const warningTexts = Array.from(
    new Set(
      Array.from(state.saveBlockers)
        .map((blocker) => blocker.warningText)
        .filter((text): text is string => text !== undefined),
    ),
  );
  const title =
    warningTexts.length > 0
      ? ["Cannot save while there are validation errors", ...warningTexts].join("\n")
      : !canSaveHierarchy
        ? "You don't have sufficient permission"
        : undefined;
  let buttonState: SaveButtonState | null = null;
  if (hierarchy && !hierarchy.isBackupVersion) {
    if (!tlf || (hierarchyState.changed && !tlfState.changed)) {
      buttonState = {
        action: "save",
        label: saveBlocked ? "Fix errors before saving" : `Save ${hierarchy.name}`,
        show: saveBlocked || hierarchyState.changed,
        disabled: saveBlocked || !canSaveHierarchy,
        title,
      };
    } else {
      const canWriteTlf = hierarchyState.accessRights.includes(
        eAccessRights.WriteToTlfItemsInHierarchy,
      );
      let label: SaveLabels | null = null;
      if (tlfState.changed) {
        if (
          tlf.id.startsWith("new") &&
          state.applicationPermissions.includes(eApplicationPermission.UpdateStandardsSettings) &&
          hierarchy.level === eHierarchyLevel.Corporate
        )
          label = SaveLabels.TlfUpdateTemplate;
        else if (tlf.domain === "BL" && hierarchy.level === eHierarchyLevel.Report)
          label = SaveLabels.TlfCannotSaveBlDomain;
        else if (tlf.id.startsWith("new")) label = SaveLabels.TlfCannotSaveNew;
        else if (!isCrystalReferenceValid(tlf.crystalReference))
          label = SaveLabels.TlfCannotSaveCrystalReference;
        else if (
          canWriteTlf &&
          (tlf.releaseStatus === eReleaseStatus.Draft || hierarchy.level === eHierarchyLevel.Report)
        )
          label = SaveLabels.Save;
        else if (canWriteTlf && tlf.releaseStatus === eReleaseStatus.ReleasePending)
          label = SaveLabels.TlfSaveAndRevert;
        else if (canWriteTlf) label = SaveLabels.TlfCannotSaveReleased;
      }
      if (label !== null) {
        buttonState = {
          action: label === SaveLabels.TlfUpdateTemplate ? "template" : "save",
          label,
          show: true,
          disabled: [
            SaveLabels.TlfCannotSaveNew,
            SaveLabels.TlfCannotSaveReleased,
            SaveLabels.TlfCannotSaveBlDomain,
            SaveLabels.TlfCannotSaveCrystalReference,
          ].includes(label),
          variant: [SaveLabels.TlfSaveAndRevert, SaveLabels.TlfUpdateTemplate].includes(label)
            ? "warning"
            : label === SaveLabels.Save
              ? "primary"
              : "default",
        };
      }
    }
  }
  return { buttonState, patchTlf, save, restore, saveTemplate, saveTocItems, saveRequest };
};
